import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import stylesGenerator from './styles';
import withStyles from '../withStyles';
import ValidationErrors from '../ValidationErrors';
import Label from '../Label';
import SpellCheckTextArea from '../SpellCheck';
import { isSpellCheckField } from '../SpellCheck/config';
import {
  formElementPropTypes,
  formElementDefaultProps,
  csePropTypes
} from '../propTypes';

/*
 * Set the `controlled` prop to false at the form model level to capture input changes with onBlur.
 * This will force the updating of the Redux store only once the input loses focus, rather than on key press.
 */
class Input extends PureComponent {
  static propTypes = {
    ...formElementPropTypes,
    ...csePropTypes,
    value: PropTypes.string,
    children: PropTypes.element.isRequired,
    'data-id': PropTypes.string, // Only provided by non-CSE inputs
    // TextArea only: enables harper.js spell & grammar checking
    spellCheck: PropTypes.bool
  };

  static defaultProps = {
    ...formElementDefaultProps,
    layout: { width: '100%' },
    styles: { width: '100%' },
    value: '',
    spellCheck: false
  };

  constructor(props) {
    super(props);
    this.state = { value: props.value || '', lastUpdate: Date.now() };
  }

  componentWillReceiveProps(props) {
    if (props.value !== this.state.value) {
      this.setState({ value: props.value, lastUpdate: Date.now() });
    }
  }

  focus = () => document.getElementById(this.props.id).focus();

  handleChange = e => {
    const { target } = e;
    const { onChange } = this.props;

    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    this.setState({ value: target.value, lastUpdate: Date.now() });
    onChange(target.value);
  };

  // Applies a spell check suggestion as if it had been typed
  handleReplace = value => {
    const { onChange, onBlur } = this.props;
    this.setState({ value, lastUpdate: Date.now() });
    onChange(value);
    onBlur(value);
  };

  handleBlur = e => {
    const { onBlur } = this.props;
    const { target } = e;
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    onBlur(target.value);
  };

  render() {
    const {
      $id,
      component,
      schemaPath,
      id,
      type,
      helpText,
      errors,
      placeholder,
      computedStyles,
      autoFocus,
      disabled,
      min,
      max,
      onKeyDown,
      maxLength
    } = this.props;

    const inputProps = {
      id,
      type,
      onChange: this.handleChange,
      onBlur: this.handleBlur,
      disabled,
      autoFocus,
      placeholder,
      ref: el => (this.component = el),
      className: `pt-input ${computedStyles.input}`,
      value: this.state.value || '',
      'aria-labelledby': `label-${id}`,
      min,
      max,
      onKeyDown,
      maxLength
    };

    const isTextArea =
      component === 'TextArea' || inputProps.type === 'textarea';
    const spellCheck = isTextArea && isSpellCheckField(this.props);

    return (
      <div
        className={computedStyles.base}
        role="group"
        data-id={$id || this.props['data-id']}
        data-schema-path={schemaPath}
      >
        <Label {...this.props} />
        {spellCheck ? (
          <SpellCheckTextArea
            inputProps={inputProps}
            onReplace={this.handleReplace}
          />
        ) : null}
        {isTextArea && !spellCheck ? <textarea {...inputProps} /> : null}
        {isTextArea ? null : (
          <input
            type="text"
            min={min}
            max={max}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            {...inputProps}
          />
        )}
        {helpText ? (
          <span className={computedStyles.helpText}>{helpText}</span>
        ) : null}
        <ValidationErrors
          id={id}
          computedStyles={computedStyles}
          validationErrors={errors}
        />
      </div>
    );
  }
}

export default withStyles(stylesGenerator)(Input);
